import React, { useEffect, useState } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import { StatCard } from '../components/dashboard/stat-card.component';
import { InvoiceTable } from '../components/dashboard/invoice-table.component';
import { BarChart } from '../components/dashboard/bar-chart.component';
import { DashboardButton } from '../components/dashboard/dashboard-button.component';
import { Sidebar } from '../components/Sidebar';
import '../scss/dashboard.style.scss';
import { InvoiceConfig } from '../components/invoice';
import WelcomeHeader from '../components/WelcomeHeader';
import Header from '../components/header/Header';
import { LoyaltySidebar, createMockLoyaltyProvider } from '../components/LoyaltySidebar';
import AppTour from '../components/AppTour/app-tour.component';
import type { TourStep } from '../components/AppTour/app-tour.component';
import WelcomePopupModal from '../components/modal/onboarding/welcome-popup-modal.component';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { getUserOnboarding } from '../../redux/auth/authSlice';
import { AppDispatch } from '../store';
import { useOnboardingStatus } from '../hooks/useOnboardingStatus';
import { PlusIcon, WaveIcon } from '../assets/dashboard-icon.svg';
import InvozioAppTour from '../components/invozio-app-tour.component';
import { fetchPersonalData } from '../features/profile-settings/profile-settings.slice';

export const DashboardPage: React.FC = () => {
    const navigate = useNavigate();
    const lang = new URLSearchParams(window.location.search).get('lang') || 'en-us';
    const [isTourOpen, setIsTourOpen] = useState(false);
    const [showWelcomeModal, setShowWelcomeModal] = useState(false);
    const [isSidebarOpen, setIsSidebarOpen] = useState(false);
    const [currentTourStep, setCurrentTourStep] = useState(0);
    const { userOnboardingData, loading } = useSelector((state: any) => state.auth);
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const { isOnboardingIncomplete, isNewUserSkipped, isSkipForNow } = useOnboardingStatus(userOnboardingData);

    const isMobileScreen = (): boolean => {
        return window.innerWidth <= 768;
    };

    useEffect(() => {
        if (isTourOpen) {
            if (isMobileScreen() && currentTourStep < 2) {
                setIsSidebarOpen(true);
            } else if (isMobileScreen() && currentTourStep >= 2) {
                setIsSidebarOpen(false);
            }
        }
    }, [isTourOpen, currentTourStep]);

    useEffect(() => {
        if (isTourOpen && currentTourStep === 3) {
            const timer = setTimeout(() => {
                const loyaltyTier = document.querySelector('.wrapper-for-loyalty-header-section');
                if (loyaltyTier) {
                    loyaltyTier.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [isTourOpen, currentTourStep]);

    useEffect(() => {
        if (isTourOpen && currentTourStep === 4) {
            const timer = setTimeout(() => {
                const loyaltySection = document.querySelector('#logo-upload-section');
                if (loyaltySection) {
                    loyaltySection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [isTourOpen, currentTourStep]);

    useEffect(() => {
        if (!loading && isOnboardingIncomplete) {
            setShowWelcomeModal(true);
        }
    }, [loading, isOnboardingIncomplete]);

    const handleClick = () => {
        const currentLang = lang || 'en-us';
        const url = currentLang === 'sv-se' ? '/v2/create-invoice?lang=sv-se' : '/v2/create-invoice?lang=en-us';
        navigate(url);
    };

    const tourSteps: TourStep[] = [
        {
            id: 'sidebar-dashboard',
            selector: '[data-testid="menu-item-dashboard"]',
            content: (
                <div>
                    <strong style={{
                        fontSize: '20px',
                    }}>{t('PERSONALIZED_SIDEBAR_DASHBOARD')}</strong>
                    <div className='py-4'>{t('PERSONALIZED_SIDEBAR_DASHBOARD_DESC')}</div>
                </div>
            ),
            position: isMobileScreen() ? 'bottom' : 'right',
            offset: { x: isMobileScreen() ? 10 : 24, y: isMobileScreen() ?  10: 10 }
        },
        {
            id: 'sidebar-invoices',
            selector: '[data-testid="menu-item-invoices"]',
            content: (
                <div>
                    <strong>{t('TOUR_SIDEBAR_INVOICES_TITLE')}</strong>
                    <div className='py-4' style={{ marginTop: 6 }}>{t('TOUR_SIDEBAR_INVOICES_DESC')}</div>
                </div>
            ),
            position: isMobileScreen() ? 'bottom' : 'right',
            offset: { x: isMobileScreen() ? 10 : 24, y: isMobileScreen() ?  10: 8 }
        },
        {
            id: 'header-create-invoice',
            selector: '.header__create-button',
            content: (
                <div>
                    <strong>{t('HEADER_CREATE_INVOICE')}</strong>
                    <div className='py-4'>{t('TOUR_CREATE_INVOICE_DESC')}</div>
                </div>
            ),
            position:'bottom',
            offset: { x: isMobileScreen() ? 10 : 0, y: isMobileScreen() ?  10: 12 }
        },
        {
            id: 'loyalty-sidebar__header-section_wrapper',
            selector: '.wrapper-for-loyalty-header-section',
            content: (
                <div>
                    <strong>{t('LOYALTY_TIER_TITLE_TOUR')}</strong>
                    <div className='py-4'>{t('TOUR_LOYALTY_TIER_DESC')}</div>
                </div>
            ),
            position: isMobileScreen() ? 'bottom' : 'left',
            offset: { x: isMobileScreen() ? 10 : -24, y: isMobileScreen() ?  0: -8 }
        },
        ...(!isSkipForNow ? [{
            id: 'logo-upload-section',
            selector: '#logo-upload-section',
            content: (
                <div>
                    <strong>{t('UPLOAD_YOUR_LOGO')}</strong>
                    <div style={{ marginTop: 6 }}>{t('UPLOAD_YOUR_LOGO_DESC_TOUR')}</div>
                </div>
            ),
            position: isMobileScreen() ? 'bottom' : 'top' as const,
            offset: { x: isMobileScreen() ? 10 : 24, y: isMobileScreen() ?  0: -5 }
        }] : [])
    ];
    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await dispatch(getUserOnboarding());
                const userId = res?.payload?.id;

                if (userId) {
                    dispatch(fetchPersonalData(userId));
                }
            } catch (e) {
                console.error('Failed to dispatch getUserOnboarding', e);
            }
        };

        fetchData();
    }, [dispatch , isSkipForNow]);

    return (
        <div className="dashboard-page">
            <div className="dashboard-layout">
                <WelcomePopupModal
                    userType={userOnboardingData?.accountType}
                    setShowWelcomeModal={() => setShowWelcomeModal(false)}
                    close={() => setShowWelcomeModal(false)}
                    handleStartTour={() => {
                        setShowWelcomeModal(false);
                        if (isMobileScreen()) {
                            setIsSidebarOpen(true);
                        }
                        setIsTourOpen(true);
                    }}
                    show={!loading && isOnboardingIncomplete}
                />
                <Sidebar
                    activeItemId="dashboard"
                    footer={null}
                    onClose={() => setIsSidebarOpen(false)}
                    className={`dashboard-sidebar ${isSidebarOpen ? 'sidebar-open' : ''}`}
                />
                {isSidebarOpen && (
                    <div
                        className="sidebar-overlay"
                        onClick={() => setIsSidebarOpen(false)}
                    />
                )}
                <AppTour
                    steps={tourSteps}
                    isOpen={isTourOpen}
                    onClose={() => setIsTourOpen(false)}
                    onStepChange={(stepIndex) => setCurrentTourStep(stepIndex)}
                />
                <div className="dashboard-main-content">
                    <Header
                        setIsSidebarOpen={setIsSidebarOpen}
                    />
                    <main className="dashboard-main">
                        <Container className="dashboard-container px-0">
                            <div className='px-3 py-2'>
                                {!isNewUserSkipped &&
                                    <InvozioAppTour
                                        onStartTour={() => {
                                            setIsTourOpen(true);
                                        }}
                                    />
                                }
                            </div>
                            {/* Welcome Section */}
                            <section className="dashboard-welcome">
                                <div className="dashboard-welcome-wrapper">
                                    {/* Left Side - Main Welcome Content */}
                                    <div className="dashboard-welcome-main">
                                        <div className="welcome-header mb-0">
                                            <div className="welcome-avatar d-none d-md-flex">
                                                <WaveIcon />
                                            </div>
                                            <div className="welcome-content">
                                                <h1 className="welcome-title ">{t('HELLO')}, {userOnboardingData?.firstName}</h1>
                                                {/* <p className="welcome-company">{getItem('userType')}</p> */}
                                            </div>
                                        </div>
                                        <div className='welcome-invoice-config'>
                                            {!isSkipForNow && (
                                                <>
                                                    <WelcomeHeader t={t} />
                                                    <InvoiceConfig showHeader={false} showPreview={true} className="compact mb-4" />
                                                </>
                                            )}
                                        </div>
                                        <StatCard
                                            title={t('TOTAL_RECEIVABLES')}
                                            className="receivables-card"
                                        >
                                            <DashboardButton
                                                variant="primary"
                                                icon={<PlusIcon />}
                                                onClick={handleClick}
                                            >
                                                {t('CREATE_INVOICE')}
                                            </DashboardButton>
                                        </StatCard>

                                        <section className="dashboard-content">
                                            <div className="dashboard-section">
                                                <InvoiceTable
                                                    className="invoices-section"
                                                />
                                            </div>
                                            <Row className="dashboard-charts">
                                                <Col lg={12} className="chart-column">
                                                    <div className="chart-card bar-chart-card">
                                                        <div className="chart-card-content">
                                                            <BarChart />
                                                        </div>
                                                    </div>
                                                </Col>
                                            </Row>
                                        </section>
                                    </div>
                                    <div className="dashboard-welcome-sidebar">
                                        <LoyaltySidebar
                                            dataProvider={createMockLoyaltyProvider()}
                                            className="dashboard-loyalty-sidebar__component"
                                        />
                                    </div>
                                </div>
                            </section>
                        </Container>
                    </main>

                </div>
            </div>
        </div>
    );
};
